import Typography from '@mui/material/Typography';
import Column from 'components/Layout/Column';
import Row from 'components/Layout/Row';
import TableViewMoreFooter from 'components/new/TableViewMoreFooter';
import Table, { type Column as TableColumn } from 'further-ui/components/Table';
import TypographyButton from 'further-ui/components/TypographyButton';
import { LpFeeCalculationBasisLabels } from 'further-ui/labels';
import { numberToCurrencyString } from 'further-ui/utils';
import { useMemo, useState } from 'react';
import { match } from 'ts-pattern';
import { CommitmentData } from '../../types';

type Props = Pick<
  CommitmentData,
  'feesAndCharges' | 'futureFeesAndCharges' | 'currencySettings'
>;

const FeesAndCharges: React.FC<Props> = ({
  feesAndCharges,
  futureFeesAndCharges,
  currencySettings,
}) => {
  const { currencyLcy } = currencySettings;
  const [showFutureFees, setShowFutureFees] = useState(false);
  const maxRows = 2;
  const [showAllRows, setShowAllRows] = useState({
    fees: false,
    futureFees: false,
  });

  const toggleExpanded = (key: keyof typeof showAllRows) => {
    setShowAllRows((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const toggleFutureFees = () => {
    setShowFutureFees((prev) => !prev);
    setShowAllRows((prev) => ({
      ...prev,
      futureFees: false,
    }));
  };

  const columns: Array<TableColumn<(typeof feesAndCharges)[number]>> = [
    {
      label: 'Fee name',
      key: 'name',
      width: 200,
      render: ({ name }) => name,
    },
    {
      label: 'Accrual date',
      key: 'date',
      width: 140,
      render: ({ date }) => date.toReadable(),
    },
    {
      label: 'Calculation basis',
      key: 'calculationBasis',
      width: 170,
      render: ({ calculationBasis }) =>
        LpFeeCalculationBasisLabels.get(calculationBasis),
    },
    {
      label: 'Fee',
      key: 'valueExTaxLcy',
      width: 140,
      render: ({ valueExTaxLcy }) =>
        numberToCurrencyString(valueExTaxLcy, {
          currency: currencyLcy,
        }),
    },
    {
      label: 'Sales tax',
      key: 'salesTaxValueLcy',
      width: 140,
      render: ({ salesTaxValueLcy }) =>
        numberToCurrencyString(salesTaxValueLcy, {
          currency: currencyLcy,
        }),
    },
    {
      label: 'Total charge',
      key: 'valueIncTaxLcy',
      width: 140,
      render: ({ valueIncTaxLcy }) =>
        numberToCurrencyString(valueIncTaxLcy, {
          currency: currencyLcy,
        }),
    },
    {
      label: 'Status',
      key: 'status',
      width: 140,
      render: ({ status }) => status,
    },
  ];

  const futureColumns = useMemo(
    () =>
      columns.map((col) =>
        match(col.key)
          .with('date', () => ({ ...col, label: 'Fee date' }))
          .with('valueExTaxLcy', () => ({ ...col, label: 'Estimated fee' }))
          .otherwise(() => col),
      ),
    [columns],
  );

  return (
    <Column spacing="xl">
      <Table
        emptyMessage="No fees and charges"
        rows={
          showAllRows.fees ? feesAndCharges : feesAndCharges.slice(0, maxRows)
        }
        scrollable
        columns={columns}
        customFooterRow={
          feesAndCharges.length > maxRows
            ? [
                {
                  key: 'fees',
                  content: (
                    <Row justify="center" fullWidth>
                      <TableViewMoreFooter
                        isExpanded={showAllRows.fees}
                        totalRows={feesAndCharges.length}
                        onExpandToggle={() => toggleExpanded('fees')}
                        unexpandedRowLimit={maxRows}
                      />
                    </Row>
                  ),
                  colspan: columns.length,
                },
              ]
            : undefined
        }
      />
      <Column spacing="sm">
        <Row spacing="xs" centered>
          {showFutureFees ? (
            <Typography variant="h5">Future fee breakdown</Typography>
          ) : null}
          <TypographyButton
            variant="body2"
            disableRipple
            onClick={toggleFutureFees}
            text={showFutureFees ? 'Hide' : 'Show future fee breakdown'}
          />
        </Row>
        {showFutureFees ? (
          <Table
            rows={
              showAllRows.futureFees
                ? futureFeesAndCharges
                : futureFeesAndCharges.slice(0, maxRows)
            }
            emptyMessage="No future fees and charges"
            scrollable
            columns={futureColumns}
            customFooterRow={
              futureFeesAndCharges.length > maxRows
                ? [
                    {
                      key: 'futureFees',
                      content: (
                        <TableViewMoreFooter
                          isExpanded={showAllRows.futureFees}
                          totalRows={futureFeesAndCharges.length}
                          onExpandToggle={() => toggleExpanded('futureFees')}
                          unexpandedRowLimit={maxRows}
                        />
                      ),
                      colspan: futureColumns.length,
                    },
                  ]
                : undefined
            }
          />
        ) : null}
      </Column>
    </Column>
  );
};

export default FeesAndCharges;
